import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Paper, Container, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Collapse, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon, } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import LineChartsale from "components/Visualizations/LineChartsale"
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import FilterChip from "components/FilterChip/FilterChip";
import { fetchFinancials } from "services/financial.service";
import { getTimeSeriesLabels } from "../../../components/TimeSeries/monthUtils";
import { useFilterContext } from "../../../../src/context/FilterContext";
import { fetchFinancialsFilter } from "../../../services/financialsFilter.service";

const FinancialDashboard = ({ subcategoryToken: propSubcategoryToken }) => {

  const {
    lastYearSameMonth,
    currentMonthLabel,
    prevMonthLabel
  } = getTimeSeriesLabels();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

  // State for API data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialsData, setFinancialsData] = useState(null);

  // Filter state for Highlights section time period chips
  const [highlightsActiveIdx, setHighlightsActiveIdx] = React.useState(0);
  const [highlightsActiveChipName, setHighlightsActiveChipName] = useState("YTD");
  const [myDashboardfilterData, setMyDashboardfilterData] = useState(null);

  // Filter state for Visualizations section time period chips
  const [visualizationActiveIdx, setVisualizationActiveIdx] = React.useState(0);
  const [visualizationActiveChipName, setVisualizationActiveChipName] = useState("YTD");

  const { setFilterOptions, setFilterChips, filterOptions, setActiveFilters, activeFilters } = useFilterContext();

  useEffect(() => {
    const header = document.querySelector('.MuiAppBar-root');
    if (header) {
      header.style.display = 'none';
    }
  }, []);

  // Helper function to build GraphQL filters from activeFilters
  const buildGqlFiltersFromActive = useCallback((filters) => {
    const extract = (items) => {
      if (!items || !Array.isArray(items) || items.length === 0) return null;
      // Handle both string arrays and object arrays with name property
      const names = items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean);
      return names.length > 0 ? { in: names } : null;
    };

    const gqlFiltersRaw = {
      companyName: extract(filters?.Company),
      categoryName: extract(filters?.Category),
      brandName: extract(filters?.Brand),
      subBrandSubcategory: extract(filters?.["Sub-Brand"]),
      subBrandName: extract(filters?.["Sub-Brand"]),
      routeToMarket: extract(filters?.["Route to Market"]),
      channel: extract(filters?.Channel)
    };
    // Remove null/undefined values to avoid backend errors
    return Object.fromEntries(
      Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
    );
  }, []);

  // Fetch filter options on initial load
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (filterOptions && Object.keys(filterOptions).length > 0) {
        return; // Already have filter options
      }

      try {
        const financialfilterResponse = await fetchFinancialsFilter();
        const filterOptionsData = {
          Company: financialfilterResponse?.companies?.items || [],
          Category: financialfilterResponse?.categories?.items || [],
          Brand: financialfilterResponse?.brands?.items || [],
          "Sub-Brand": financialfilterResponse?.subBrands?.items || [],
          "Route to Market": financialfilterResponse?.routeToMarkets?.items || [],
          Channel: financialfilterResponse?.channels?.items || [],
        };
        setFilterOptions(filterOptionsData);
        setMyDashboardfilterData(financialfilterResponse);

        const companyChips = financialfilterResponse?.companies.items || [];
        const categoryChips = financialfilterResponse?.categories?.items || [];
        setFilterChips([
          { key: "all", name: "All" },
          ...companyChips,
          ...categoryChips,
          { key: "custom", name: "Custom" }
        ]);
      } catch (error) {
        console.error('Failed loading filter options:', error);
      }
    };

    fetchFilterOptions();
  }, [filterOptions, setFilterOptions, setFilterChips]);

  // Fetch dashboard data when filters or time periods change
  useEffect(() => {
    const fetchAllData = async () => {
      // Wait for filter options to be available if they're not loaded yet
      if (!filterOptions || Object.keys(filterOptions).length === 0) {
        return;
      }

      try {
        setLoading(true);

        // Build GraphQL filters from activeFilters if they exist and have selections
        // Otherwise, use all available filter options
        let gqlFilters = {};

        if (activeFilters && Object.values(activeFilters).some(arr => (arr || []).length > 0)) {
          // Use activeFilters when they have selections
          gqlFilters = buildGqlFiltersFromActive(activeFilters);
        } else {
          // Fallback to all filter options if no active filters
          const extractNames = (items) => {
            if (!items?.length) return null;
            return {
              in: items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean)
            };
          };

          const gqlFiltersRaw = {
            companyName: extractNames(filterOptions.Company),
            categoryName: extractNames(filterOptions.Category),
            brandName: extractNames(filterOptions.Brand),
            subBrandSubcategory: extractNames(filterOptions["Sub-Brand"]),
            subBrandName: extractNames(filterOptions?.["Sub-Brand"]),
            routeToMarket: extractNames(filterOptions["Route to Market"]),
            channel: extractNames(filterOptions.Channel)
          };
          gqlFilters = Object.fromEntries(
            Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
          );
        }

        console.log('Fetching dashboard data with filters:', gqlFilters);
        const financialResponse = await fetchFinancials(
          visualizationActiveChipName,
          highlightsActiveChipName,
          gqlFilters
        );

        const financialsItem = financialResponse || null;
        setFinancialsData(financialsItem);

      } catch (error) {
        console.error('Failed loading financial data:', error);
        setError('Failed to load financial data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [location.search, propSubcategoryToken, visualizationActiveChipName, highlightsActiveChipName, activeFilters, buildGqlFiltersFromActive, filterOptions]);




  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        // Call fetchFinancials API and store first item
        // Use visualizationActiveChipName for grossProfitByCategory query
        // Use highlightsActiveChipName for profitability queries
        const extractNames = (items) => {
          if (!items?.length) return null;

          return {
            in: items.map(item => item.name)
          };
        };
        const financialfilterResponse = await fetchFinancialsFilter();
        console.log('Financial Filter API Response:', financialfilterResponse);
        const gqlFilters = {
          companyName: extractNames(financialfilterResponse?.companies?.items),
          categoryName: extractNames(financialfilterResponse?.categories?.items),
          brandName: extractNames(financialfilterResponse?.brands?.items),
          subBrandSubcategory: extractNames(financialfilterResponse?.subBrands?.items),
          routeToMarket: extractNames(financialfilterResponse?.routeToMarkets?.items),
          channel: extractNames(financialfilterResponse?.channels?.items)
        };

        const filterOptions = {
          Company: financialfilterResponse?.companies.items,
          Category: financialfilterResponse?.categories?.items,
          Brand: financialfilterResponse?.brands?.items,
          "Sub-Brand": financialfilterResponse?.subBrands?.items,
          "Route to Market": financialfilterResponse?.routeToMarkets?.items,
          Channel: financialfilterResponse?.channels?.items,
        };
        const financialsResponse = await fetchFinancials(visualizationActiveChipName, highlightsActiveChipName, gqlFilters);
        console.log('Financials API Response:', financialsResponse);

        const financialsItem = financialsResponse || null;
        setFinancialsData(financialsItem);
        setMyDashboardfilterData(financialfilterResponse);
        setFilterOptions(filterOptions);
        const companyChips = financialfilterResponse?.companies.items || [];
        const categoryChips = financialfilterResponse?.categories?.items || [];

        setFilterChips([
          { key: "all", name: "All" },
          ...companyChips,
          ...categoryChips,
          { key: "custom", name: "Custom" }
        ]);
      } catch (error) {
        console.error('Failed loading financials data:', error);
        setError('Failed to load financials data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [visualizationActiveChipName, highlightsActiveChipName, myDashboardfilterData]);


  const formatToMillions = (value) =>
    value == null || value === "-" ? "-" : (Number(value) / 1_000_000).toFixed(1);

  const truncateDecimal = (num, decimals = 1) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(num * factor) / factor;
  };
  //First section KPIscards data
  const Kpiscardsdata = [
    {
      title: "Net Sales",
      showIndicator: true,
      values: [
        {
          label: "MTD",
          change: financialsData?.netSales?.items?.cMvsGoal ? `${financialsData?.netSales?.items?.cMvsGoal}%` : "0%",
          changely: financialsData?.netSales?.items?.cMvsLM ? `${financialsData?.netSales?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.netSales?.items?.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(financialsData?.netSales?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(financialsData?.netSales?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: financialsData?.netSales?.items?.cQvsGoal ? `${financialsData?.netSales?.items?.cQvsGoal}%` : "0%",
          changely: financialsData?.netSales?.items?.cQvsLQ ? `${financialsData?.netSales?.items?.cQvsLQ}%` : "0%",
          value: `$${formatToMillions(financialsData?.netSales?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(financialsData?.netSales?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(financialsData?.netSales?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: financialsData?.netSales?.items?.cYvsGoal ? `${financialsData?.netSales?.items?.cYvsGoal}%` : "0%",
          changely: financialsData?.netSales?.items?.cYvsLY ? `${financialsData?.netSales?.items?.cYvsLY}%` : "0%",
          value: `$${formatToMillions(financialsData?.netSales?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(financialsData?.netSales?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(financialsData?.netSales?.items?.goalCurrentYear)}M`
        },
      ]
    },
    {
      title: "Cost of Goods Sold",
      showIndicator: true,
      values: [
        {
          label: "MTD",
          change: financialsData?.costOfGoodsSold?.items?.cMvsGoal ? `${financialsData?.costOfGoodsSold?.items?.cMvsGoal}%` : "0%",
          changely: financialsData?.costOfGoodsSold?.items?.cMvsLM ? `${financialsData?.costOfGoodsSold?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: financialsData?.costOfGoodsSold?.items?.cQvsGoal ? `${financialsData?.costOfGoodsSold?.items?.cQvsGoal}%` : "0%",
          changely: financialsData?.costOfGoodsSold?.items?.cQvsLM ? `${financialsData?.costOfGoodsSold?.items?.cQvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: financialsData?.costOfGoodsSold?.items?.cYvsGoal ? `${financialsData?.costOfGoodsSold?.items?.cYvsGoal}%` : "0%",
          changely: financialsData?.costOfGoodsSold?.items?.cYvsLM ? `${financialsData?.costOfGoodsSold?.items?.cYvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Gross Profit",
      showIndicator: true,
      values: [
        {
          label: "MTD",
          change: financialsData?.grossProfit?.items?.cMvsGoal ? `${financialsData?.grossProfit?.items?.cMvsGoal}%` : "0%",
          changely: financialsData?.grossProfit?.items?.cMvsLM ? `${financialsData?.grossProfit?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.grossProfit?.items?.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(financialsData?.grossProfit?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(financialsData?.grossProfit?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: financialsData?.grossProfit?.items?.cQvsGoal ? `${financialsData?.grossProfit?.items?.cQvsGoal}%` : "0%",
          changely: financialsData?.grossProfit?.items?.cQvsLM ? `${financialsData?.grossProfit?.items?.cQvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.grossProfit?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(financialsData?.grossProfit?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(financialsData?.grossProfit?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: financialsData?.grossProfit?.items?.cYvsGoal ? `${financialsData?.grossProfit?.items?.cYvsGoal}%` : "0%",
          changely: financialsData?.grossProfit?.items?.cYvsLM ? `${financialsData?.grossProfit?.items?.cYvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.grossProfit?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(financialsData?.grossProfit?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(financialsData?.grossProfit?.items?.goalCurrentYear)}M`
        }
      ]
    },

    // ROW 2
    {
      title: "eBit",
      showIndicator: true,
      values: [
        {
          label: "MTD",
          change: financialsData?.eBit?.items?.cMvsGoal ? `${financialsData?.eBit?.items?.cMvsGoal}%` : "0%",
          changely: financialsData?.eBit?.items?.cMvsLM ? `${financialsData?.eBit?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.eBit?.items?.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(financialsData?.eBit?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(financialsData?.eBit?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: financialsData?.eBit?.items?.cQvsGoal ? `${financialsData?.eBit?.items?.cQvsGoal}%` : "0%",
          changely: financialsData?.eBit?.items?.cQvsLM ? `${financialsData?.eBit?.items?.cQvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.eBit?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(financialsData?.eBit?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(financialsData?.eBit?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: financialsData?.eBit?.items?.cYvsGoal ? `${financialsData?.eBit?.items?.cYvsGoal}%` : "0%",
          changely: financialsData?.eBit?.items?.cYvsLM ? `${financialsData?.eBit?.items?.cYvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.eBit?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(financialsData?.eBit?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(financialsData?.eBit?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Closing Balance (Cash Position)",
      showIndicator: true,
      values: [
        {
          label: "MTD",
          change: financialsData?.closingBalance?.items?.cMvsGoal ? `${financialsData?.closingBalance?.items?.cMvsGoal}%` : "0%",
          changely: financialsData?.closingBalance?.items?.cMvsLM ? `${financialsData?.closingBalance?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.closingBalance?.items?.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(financialsData?.closingBalance?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(financialsData?.closingBalance?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: financialsData?.closingBalance?.items?.cQvsGoal ? `${financialsData?.closingBalance?.items?.cQvsGoal}%` : "0%",
          changely: financialsData?.closingBalance?.items?.cQvsLM ? `${financialsData?.closingBalance?.items?.cQvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.closingBalance?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(financialsData?.closingBalance?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(financialsData?.closingBalance?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: financialsData?.closingBalance?.items?.cYvsGoal ? `${financialsData?.closingBalance?.items?.cYvsGoal}%` : "0%",
          changely: financialsData?.closingBalance?.items?.cYvsLM ? `${financialsData?.closingBalance?.items?.cYvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.closingBalance?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(financialsData?.closingBalance?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(financialsData?.closingBalance?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      title: "Stock Amount (Dollars)",
      showIndicator: true,
      values: [
        {
          label: "MTD",
          change: financialsData?.stockAmount?.items?.cMvsGoal ? `${financialsData?.stockAmount?.items?.cMvsGoal}%` : "0%",
          changely: financialsData?.stockAmount?.items?.cMvsLM ? `${financialsData?.stockAmount?.items?.cMvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.stockAmount?.items?.currentMonth)}M`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(financialsData?.stockAmount?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(financialsData?.stockAmount?.items?.goalCurrentMonth)}M`
        },
        {
          label: "QTD",
          change: financialsData?.stockAmount?.items?.cQvsGoal ? `${financialsData?.stockAmount?.items?.cQvsGoal}%` : "0%",
          changely: financialsData?.stockAmount?.items?.cQvsLM ? `${financialsData?.stockAmount?.items?.cQvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.stockAmount?.items?.currentQuarter)}M`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(financialsData?.stockAmount?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(financialsData?.stockAmount?.items?.goalCurrentQuarter)}M`
        },
        {
          label: "YTD",
          change: financialsData?.stockAmount?.items?.cYvsGoal ? `${financialsData?.stockAmount?.items?.cYvsGoal}%` : "0%",
          changely: financialsData?.stockAmount?.items?.cYvsLM ? `${financialsData?.stockAmount?.items?.cYvsLM}%` : "0%",
          value: `$${formatToMillions(financialsData?.stockAmount?.items?.currentYear)}M`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(financialsData?.stockAmount?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(financialsData?.stockAmount?.items?.goalCurrentYear)}M`
        }
      ]
    }
  ];

  console.log('time series data', financialsData)
  //Second section TimeSeriescards data
  const timeSeriesCardsdata = [
    {
      title: "Net Sales",
      values: [
        {
          label: lastYearSameMonth,
          change: financialsData?.netSales?.items?.cmcYvsCMLY ? `${truncateDecimal(financialsData?.netSales?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(financialsData?.netSales?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(financialsData?.netSales?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: financialsData?.netSales?.items?.cMvsLM ? `${truncateDecimal(financialsData?.netSales?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(financialsData?.netSales?.items?.lastMonth)}M`,
          status: "decrease",
          arrow: "left"
        }
      ],
      "showIndicator": true,
    },

    {
      title: "Cost of Goods Sold",
      values: [
        {
          label: lastYearSameMonth,
          change: financialsData?.costOfGoodsSold?.items?.cmcYvsCMLY ? `${truncateDecimal(financialsData?.costOfGoodsSold?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: financialsData?.costOfGoodsSold?.items?.cMvsLM ? `${truncateDecimal(financialsData?.costOfGoodsSold?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(financialsData?.costOfGoodsSold?.items?.lastMonth)}M`,
          status: "decrease",
          arrow: "left"
        }
      ],
      "showIndicator": true,
    },

    {
      title: "Gross Profit",
      values: [
        {
          label: lastYearSameMonth,
          change: financialsData?.grossProfit?.items?.cmcYvsCMLY ? `${truncateDecimal(financialsData?.grossProfit?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(financialsData?.grossProfit?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(financialsData?.grossProfit?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: financialsData?.grossProfit?.items?.cMvsLM ? `${truncateDecimal(financialsData?.grossProfit?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(financialsData?.grossProfit?.items?.lastMonth)}M`,
          status: "decrease",
          arrow: "left"
        }
      ],
      "showIndicator": true,
    },

    {
      title: "EBIT",
      values: [
        {
          label: lastYearSameMonth,
          change: financialsData?.eBit?.items?.cmcYvsCMLY ? `${truncateDecimal(financialsData?.eBit?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(financialsData?.eBit?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(financialsData?.eBit?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: financialsData?.eBit?.items?.cMvsLM ? `${truncateDecimal(financialsData?.eBit?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(financialsData?.eBit?.items?.lastMonth)}M`,
          status: "decrease",
          arrow: "left"
        }
      ],
      "showIndicator": true,
    },

    {
      title: "Closing Balance (Cash Position)",
      values: [
        {
          label: lastYearSameMonth,
          change: financialsData?.closingBalance?.items?.cmcYvsCMLY ? `${truncateDecimal(financialsData?.closingBalance?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(financialsData?.closingBalance?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(financialsData?.closingBalance?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: financialsData?.closingBalance?.items?.cMvsLM ? `${truncateDecimal(financialsData?.closingBalance?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(financialsData?.closingBalance?.items?.lastMonth)}M`,
          status: "decrease",
          arrow: "left"
        }
      ],
      "showIndicator": true,
    },

    {
      title: "Stock Amount (Dollars)",
      values: [
        {
          label: lastYearSameMonth,
          change: financialsData?.stockAmount?.items?.cmcYvsCMLY ? `${truncateDecimal(financialsData?.stockAmount?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(financialsData?.stockAmount?.items?.sameMonthLastYear)}M`,
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(financialsData?.stockAmount?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: financialsData?.stockAmount?.items?.cMvsLM ? `${truncateDecimal(financialsData?.stockAmount?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(financialsData?.stockAmount?.items?.lastMonth)}M`,
          status: "decrease",
          arrow: "left"
        }
      ],
      "showIndicator": true,
    },
  ];

  //Third section highlightCards 
  const highlightCards = [
    // --- Sub Brand ---
    {
      title: "#1 Sub Brand by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[0]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityBySubBrand?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Sub Brand by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[1]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityBySubBrand?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Sub Brand by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[2]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityBySubBrand?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityBySubBrand?.items[2]?.lastPeriodNetSales)}M`
    },

    // --- Distributor ---
    {
      title: "#1 Distributor by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[0]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityByDistributorByFinancial?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Distributor by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[1]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityByDistributorByFinancial?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Distributor by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[2]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityByDistributorByFinancial?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityByDistributorByFinancial?.items[2]?.lastPeriodNetSales)}M`
    },

    // --- Retailer ---
    {
      title: "#1 Retailer by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[0]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityByRetailerByFinancial?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Retailer by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[1]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityByRetailerByFinancial?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Retailer by Profitability",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[2]?.netSales)}M`,
      bigTitle: financialsData?.profitabilityByRetailerByFinancial?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(financialsData?.profitabilityByRetailerByFinancial?.items[2]?.lastPeriodNetSales)}M`
    }
  ];



  const [openFilters, setOpenFilters] = useState(false);
  const handleToggleFilters = () => {
    setOpenFilters((prev) => !prev);
  };

  // Filter state
  const [activeFilter, setActiveFilter] = useState('All');
  const [filterLoading, setFilterLoading] = useState(false);

  // Handler for Highlights section chips
  const handleHighlightsChipClick = (idx, label) => {
    setHighlightsActiveIdx(idx);
    setHighlightsActiveChipName(label);
    console.log("Clicked Highlights Chip Name:", label);
  };

  // Handler for Visualizations section chips
  const handleVisualizationChipClick = (idx, label) => {
    setVisualizationActiveIdx(idx);
    setVisualizationActiveChipName(label);
    console.log("Clicked Visualization Chip Name:", label);
  };

  // Define filter presets for each quick filter
  const getFilterPreset = useCallback((filterName) => {
    if (!filterOptions) return null;

    // Helper to extract names from filter options
    const getNames = (key) => (filterOptions[key] || []).map(v => v?.name || v);

    // Get all available options dynamically
    const allCompanies = getNames("Company");
    const allCategories = getNames("Category");
    const allBrands = getNames("Brand");
    const allSubBrands = getNames("Sub-Brand");
    const allRoutes = getNames("Route to Market");
    const allChannels = getNames("Channel");

    // Helper to get Nutrabolt brands/sub-brands (exclude Bloom)
    const getNutraboltBrands = () => {
      return allBrands
        .filter(brand => {
          const brandName = typeof brand === 'string' ? brand : brand?.name;
          return brandName && !brandName.toLowerCase().includes('bloom');
        })
        .map(b => typeof b === 'string' ? b : b?.name);
    };

    const getNutraboltSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          return subBrandName && !subBrandName.toLowerCase().includes('bloom');
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Bloom brands/sub-brands
    const getBloomBrands = () => {
      return allBrands
        .filter(brand => {
          const brandName = typeof brand === 'string' ? brand : brand?.name;
          return brandName && brandName.toLowerCase().includes('bloom');
        })
        .map(b => typeof b === 'string' ? b : b?.name);
    };

    const getBloomSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          return subBrandName && subBrandName.toLowerCase().includes('bloom');
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Functional Beverage sub-brands (beverage products, exclude powders)
    const getFunctionalBeverageSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          // Exclude powder/supplement sub-brands
          return subBrandName &&
            !subBrandName.toLowerCase().includes('powder') &&
            !subBrandName.toLowerCase().includes('cellucor') &&
            subBrandName !== 'XTEND' &&
            subBrandName !== 'C4';
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Active Nutrition sub-brands (supplements/powders)
    const getActiveNutritionSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          // Include powder and supplement sub-brands
          return subBrandName && (
            subBrandName.toLowerCase().includes('powder') ||
            subBrandName === 'XTEND' ||
            subBrandName === 'C4' ||
            subBrandName.toLowerCase().includes('cellucor')
          );
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to find category by name
    const findCategory = (searchTerm) => {
      return allCategories.find(cat => {
        const catName = typeof cat === 'string' ? cat : cat?.name;
        return catName && catName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    };

    switch (filterName) {
      case 'All':
        return {
          Company: allCompanies,
          Category: allCategories,
          Brand: allBrands,
          "Sub-Brand": allSubBrands,
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Nutrabolt':
        return {
          Company: ["Nutrabolt"],
          Category: allCategories,
          Brand: getNutraboltBrands(),
          "Sub-Brand": getNutraboltSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Bloom':
        const bloomCategory = findCategory('functional');
        return {
          Company: ["Bloom"],
          Category: bloomCategory ? [typeof bloomCategory === 'string' ? bloomCategory : bloomCategory?.name] : [],
          Brand: getBloomBrands(),
          "Sub-Brand": getBloomSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Functional Beverage':
        const functionalBeverageCategory = findCategory('functional');
        const functionalBeverageBrands = allBrands
          .filter(brand => {
            const brandName = typeof brand === 'string' ? brand : brand?.name;
            return brandName && (brandName === 'C4' || brandName.toLowerCase().includes('bloom'));
          })
          .map(b => typeof b === 'string' ? b : b?.name);
        return {
          Company: allCompanies,
          Category: functionalBeverageCategory ? [typeof functionalBeverageCategory === 'string' ? functionalBeverageCategory : functionalBeverageCategory?.name] : [],
          Brand: functionalBeverageBrands,
          "Sub-Brand": getFunctionalBeverageSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Active Nutrition':
        const activeNutritionCategory = findCategory('active');
        return {
          Company: ["Nutrabolt"],
          Category: activeNutritionCategory ? [typeof activeNutritionCategory === 'string' ? activeNutritionCategory : activeNutritionCategory?.name] : [],
          Brand: getNutraboltBrands(),
          "Sub-Brand": getActiveNutritionSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      default:
        return null;
    }
  }, [filterOptions]);



  const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));
  useEffect(() => {
    if (filterOptions && !filterPresets) {
      const allPreset = getFilterPreset('All');
      if (allPreset) {
        setFilterPresets(allPreset);
      }
    }
  }, [filterOptions, filterPresets, getFilterPreset]);


  // Handle filter click with full-page loader
  const handleFilterClick = (filter) => {
    if (filter === activeFilter || filterLoading) return;

    setFilterLoading(true);
    setLoading(true); // Show full-page loader

    // Simulate loading delay
    setTimeout(() => {
      const preset = getFilterPreset(filter);
      setActiveFilter(filter);

      // Set the preset - this will trigger FilterPanelContainer to apply it
      if (preset) {
        setFilterPresets(preset);
        if (setActiveFilters) {
          setActiveFilters(preset);
        }
      } else {
        setFilterPresets(null);
      }

      setFilterLoading(false);
      setLoading(false); // Hide full-page loader

      // Open filter panel for "Custom" filter
      if (filter === 'Custom') {
        setOpenFilters(true);
      }
    }, 800);
  };

  // Handle filter changes from FilterPanelContainer
  const handleFiltersChange = useCallback((newFilters) => {
    // Don't check for preset match if we're already in Custom mode
    // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
    if (activeFilter === 'Custom') return;

    // Check if the current filters match any preset
    const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
    let matchedPreset = null;

    for (const presetName of presetNames) {
      const preset = getFilterPreset(presetName);
      if (preset) {
        const isMatching = Object.keys(preset).every(key => {
          const presetValues = [...(preset[key] || [])].sort();
          const currentValues = [...(newFilters[key] || [])].sort();
          return presetValues.length === currentValues.length &&
            presetValues.every((val, idx) => val === currentValues[idx]);
        });

        if (isMatching) {
          matchedPreset = presetName;
          break;
        }
      }
    }

    // If filters don't match any preset, switch to Custom
    if (!matchedPreset && activeFilter !== 'Custom') {
      setActiveFilter('Custom');
      setFilterPresets(null);
    }
  }, [activeFilter, getFilterPreset]);




  // PIE CHART DATA 
  // Color mapping for category names
  const getCategoryColor = (categoryName) => {
    const colorMap = {
      "Active Nutrition": "#0255D6",
      "Functional Beverage": "#8CC63F"
    };
    return colorMap[categoryName] || "#000000";
  };

  // Color mapping for sub-brand names
  const getSubBrandColor = (subBrandName) => {
    const colorMap = {
      "C4 Powder": "#C2CF2D",
      "Bloom Pop": "#59A671",
      "Bloom Sparkling Energy": "#C393E4",
      "XTEND": "#C5C5C5",
      "Cellucor": "#747474",
      "C4 Ultimate": "#1A1A1A",
      "C4 Smart Energy": "#1BA4E6",
      "C4 Performance": "#ECD01C",
      "C4": "#C2CF2D" // Default to C4 Powder color if just "C4"

    };
    return colorMap[subBrandName] || "#000000";
  };


  // Transform API data to pie chart format
  const pieCategory = useMemo(() => {
    const items = financialsData?.grossProfitByCategory?.items;
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }
    return items.map(item => ({
      label: item.categoryName || "",
      value: item.percentage || 0,
      color: getCategoryColor(item.categoryName)
    }));
  }, [financialsData]);

  // Transform API data to pie chart format for sub-brands
  const pieSubBrands = useMemo(() => {
    const items = financialsData?.profitabilityBySubBrand?.items;
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }
    return items.map(item => ({
      label: item.name || "",
      value: item.percentage || 0,
      color: getSubBrandColor(item.name)
    }));
  }, [financialsData]);



  const getDistributorColor = (name) => {
    const colorMap = {
      "Other": "#000000",
      "Brown Bottling Group": "#3948AB",
      "The Lewis Bear Company": "#F4511E",
      "Great Basin Beverage": "#C52161",
      "Watkins Distributing": "#00897B",
      "The American Bottling Company (KDP)": "#7B21A3",
    };

    return colorMap[name] || "#000000";
  };
  const pieDistributorsData = useMemo(() => {
    const items = financialsData?.profitabilityByDistributorByFinancial?.items;
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }
    return items.map(item => ({
      label: item.name || "",
      value: Number(item.percentage?.toFixed(1)) || 0,
      color: getDistributorColor(item.name)
    }));

  }, [financialsData]);

  console.log(pieDistributorsData, "distributor data")

  const getRetailerColor = (retailerName) => {
    if (!retailerName) return "#000000";

    const normalized = retailerName.trim();

    const colorMap = {
      "Other": "#000000",
      "HEB": "#7B21A3",
      "Circle K": "#C52161",
      "Kroger": "#8CC63F",
      "Walmart": "#1E63DA",
      "7-Eleven": "#E3A403",
    };

    return colorMap[normalized] || "#000000";
  };

  const pieRetailersData = useMemo(() => {
    const items = financialsData?.profitabilityByRetailerByFinancial?.items;
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }
    return items.map(item => ({
      label: item.name || "",
      value: Number(item.percentage?.toFixed(1)) || 0,
      color: getRetailerColor(item.name)
    }));

  }, [financialsData]);

  console.log(pieRetailersData, "financials")

  const getSubBrandchartColor = (subBrandName) => {
    const colorMap = {
      "C4 Powder": "#C2CF2D",
      "C4 Performance": "#ECD01C",
      "C4 Smart Energy": "#1BA4E6",
      "C4 Ultimate": "#1A1A1A",
      "Cellucor": "#747474",
      "XTEND": "#C5C5C5",
      "Bloom Sparkling Energy": "#C393E4",
      "Bloom Pop": "#59A671",
      "C4": "#C2CF2D",
      "Bloom": "#59A671"
    };

    return colorMap[subBrandName] || "#000000";
  };

  const convertSubBrandDataDynamic = useMemo(() => {
    const apiItems =
      financialsData?.netSalesBySubBrandMonthlyGrouped?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    // Sort Months Correctly
    const sortedItems = [...apiItems].sort(
      (a, b) => new Date("01-" + a.month) - new Date("01-" + b.month)
    );

    // X Labels Dynamically
    const xLabels = sortedItems.map((item) =>
      item.month.replace("-", ". ")
    );

    // Step 1: Collect ALL Unique Brand Names Dynamically
    const brandSet = new Set();

    sortedItems.forEach((monthItem) => {
      monthItem.values.forEach((v) => {
        let brand =
          v.subBrandName === "C4" ? "C4 Powder" : v.subBrandName;

        brandSet.add(brand);
      });
    });

    // Convert Set → Array
    const labels = Array.from(brandSet);

    // Step 2: Build Series Dynamically
    const series = labels.map((label) => ({
      label,
      color: getSubBrandchartColor(label)
    }));

    // Step 3: Build DataMatrix Dynamically
    const dataMatrix = sortedItems.map((monthItem) => {
      const valueMap = {};

      monthItem.values.forEach((v) => {
        let brand =
          v.subBrandName === "C4" ? "C4 Powder" : v.subBrandName;

        valueMap[brand] = v.netSalesAmount / 1_000_000;
      });

      // Row must follow labels order
      return labels.map((label) =>
        +(valueMap[label] || 0).toFixed(2)
      );
    });
    // Calculate stacked totals per month
    const stackedTotals = dataMatrix.map(row =>
      row.reduce((sum, val) => sum + val, 0)
    );

    // Get maximum stacked value
    const maxValue = Math.max(...stackedTotals);

    // Generate nice tick step
    function niceTickStep(maxVal, numTicks = 5) {
      const roughStep = maxVal / numTicks;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const residual = roughStep / magnitude;

      let niceResidual;
      if (residual >= 5) niceResidual = 5;
      else if (residual >= 2) niceResidual = 2;
      else niceResidual = 1;

      return niceResidual * magnitude;
    }
    console.log(maxValue, "maxValue")
    const tickStep = niceTickStep(maxValue, 5);
    const maxTick = tickStep * Math.ceil(maxValue / tickStep);

    const yTicks = [];
    for (let tick = 0; tick <= maxTick; tick += tickStep) {
      yTicks.push(Number(tick.toFixed(4))); 
    }

    // Final Output Structure
    return {
      title: "Net Sales by Sub Brand",
      xLabels,
      series,
      dataMatrix,
      yTicks
    };
  }, [financialsData]);



  const getNetGrossCashColor = (key) => {
    const colorMap = {
      net: "#62BDFF",
      gross: "#317FBB",
      cash: "#449C7D"
    };
    return colorMap[key] || "#000000";
  };

  const convertNetGrossCashChart = useMemo(() => {
    const apiItems =
      financialsData?.cashBalanceAndInventory?.items || [];

    if (!Array.isArray(apiItems)) return null;

    // Sort months
    const sortedItems = [...apiItems].sort(
      (a, b) => new Date("01-" + a.monthYear) - new Date("01-" + b.monthYear)
    );

    // xLabels
    const xLabels = sortedItems.map((item) =>
      item.monthYear.replace("-", ". ")
    );

    // Fields you want from API
    const selectedKeys = ["net", "gross", "cash"];

    // Dynamic Series Generation
    const series = selectedKeys.map((key) => ({
      key,

      label:
        key === "net"
          ? "Net Sales"
          : key === "gross"
            ? "Gross Profit"
            : key === "cash"
              ? "Cash Balance"
              : key,

      // Extra Property (only for cash)
      dashed: key === "cash",

      // Color from function
      color: getNetGrossCashColor(key)
    }));


    // DataMatrix
    const dataMatrix = sortedItems.map((item) => [
      +(item.netSalesAmount / 1_000_000).toFixed(2),
      +(item.grossProfitAmount / 1_000_000).toFixed(2),
      +(item.cashBalance / 1_000_000).toFixed(2)
    ]);
    // Flatten all values in dataMatrix to a single array
    const allValues = dataMatrix.flat();

    // Get the max value
    const maxValue = Math.max(...allValues, 0);

    // Function to generate nice ticks dynamically
    function generateNiceTicks(maxVal, tickCount = 5) {
      if (maxVal === 0) return [0];

      const roughStep = maxVal / tickCount;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const normalized = roughStep / magnitude;

      let niceNormalized;

      if (normalized <= 1) niceNormalized = 1;
      else if (normalized <= 2) niceNormalized = 2;
      else if (normalized <= 5) niceNormalized = 5;
      else niceNormalized = 10;

      const step = niceNormalized * magnitude;
      const maxTick = Math.ceil(maxVal / step) * step;

      const ticks = [];
      for (let i = 0; i <= maxTick + step / 2; i += step) {
        ticks.push(Number(i.toFixed(6)));
      }

      return ticks;
    }

    const yTicks = generateNiceTicks(maxValue, 5);
    return {
      title: "Net Sales, Gross Profit, and Cash Balance",
      xLabels,
      series,
      dataMatrix,
      yTicks
    };
  }, [financialsData]);
  console.log(convertNetGrossCashChart, "^^^^^^^")


  const getInventoryColor = (label) => {
    const map = {
      cashBalance: "#56BC4C",
      stockAmount: "#8A2BE2",
      cashIn: "#56BC4C",
      cashOut: "#E74C3C"
    };
    return map[label] || "#000";
  };

  const convertInventoryChartData = useMemo(() => {
    const apiItems =
      financialsData?.cashBalanceAndInventory?.items || [];

    if (!Array.isArray(apiItems) || apiItems.length === 0) return null;

    // Sort Months
    const sortedItems = [...apiItems].sort(
      (a, b) => new Date("01-" + a.monthYear) - new Date("01-" + b.monthYear)
    );

    // Dynamic X Labels
    const xLabels = sortedItems.map((item) =>
      item.monthYear.replace("-", ". ")
    );

    // Fields you want from API (Dynamic Keys)
    const selectedKeys = ["cashBalance", "stockAmount", "cashIn", "cashOut"];

    // Dynamic Series Generation
    const series = selectedKeys.map((key) => ({
      key,
      label:
        key === "cashBalance"
          ? "Cash Balance"
          : key === "stockAmount"
            ? "Stock Amount (Dollars)"
            : key === "cashIn"
              ? "Cash In"
              : key === "cashOut"
                ? "Cash Out"
                : key,

      type: key === "cashBalance" || key === "stockAmount" ? "line" : "bar",

      color: getInventoryColor(key)
    }));

    // Dynamic DataMatrix (Same Order as Series)
    const dataMatrix = sortedItems.map((item) =>
      selectedKeys.map((key) =>
        +((item[key] || 0) / 1_000_000).toFixed(2)
      )
    );
    const allValues = dataMatrix.flat(); // flatten 2D array
    const maxValue = Math.max(...allValues, 0);

    function generateNiceTicks(maxVal, tickCount = 5) {
      if (maxVal === 0) return [0];

      const roughStep = maxVal / tickCount;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const normalized = roughStep / magnitude;

      let niceNormalized;
      if (normalized <= 1) niceNormalized = 1;
      else if (normalized <= 2) niceNormalized = 2;
      else if (normalized <= 5) niceNormalized = 5;
      else niceNormalized = 10;

      const step = niceNormalized * magnitude;
      const maxTick = Math.ceil(maxVal / step) * step;

      const ticks = [];
      for (let i = 0; i <= maxTick + step / 2; i += step) {
        ticks.push(Number(i.toFixed(6)));
      }

      return ticks;
    }

    const yTicks = generateNiceTicks(maxValue, 5);

    return {
      title: "Cash Balance and Inventory Value",
      xLabels,
      series,
      dataMatrix,
      yTicks
    };
  }, [financialsData]);

  console.log(convertInventoryChartData, "$$$$$$$$$")

  const convertRevenueToEBITChart = useMemo(() => {
    const apiData = financialsData?.revenueToEBIT;

    // Safety Check
    if (!apiData?.item) return null;

    const item = apiData.item;
    const period = "cy"; // Taking CY Values

    // Correct Waterfall Step Order
    const order = [
      "GrossSales",
      "TradeSpend",
      "NetSales",
      "cogs",
      "GrossProfit",
      "OperatingExpense",
      "ebit",
    ];

    // Build Raw Data Array
    const raw = order.map((field) => {
      const key = `${period}${field}`;
      const rawValue = item[key] || 0;

      // Label Formatting
      let label = field.replace(/([A-Z])/g, " $1").trim();

      if (label.toLowerCase() === "cogs") label = "COGS";
      if (label.toLowerCase() === "ebit") label = "EBIT";

      const isNegativeBucket =
        label.includes("Spend") ||
        label.includes("Expense") ||
        label === "COGS";

      let valueInMillions = rawValue / 1_000_000;

      // Only flip sign for expense buckets that come as positive numbers
      if (isNegativeBucket && valueInMillions > 0) {
        valueInMillions = -valueInMillions;
      }

      const finalValue = Number(valueInMillions.toFixed(1));
      return {
        label,
        value: finalValue,
      };
    });
    let running = 0;
    const cumulativeValues = raw.map((r) => {
      running += r.value;
      return running;
    });

    const maxVal = Math.max(...cumulativeValues, 0);
    const minVal = Math.min(...cumulativeValues, 0);

    function generateNiceTicks(minVal, maxVal, tickCount = 5) {
      if (maxVal === minVal) return [0];

      const range = maxVal - minVal;
      const roughStep = range / tickCount;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const normalized = roughStep / magnitude;

      let niceNormalized;
      if (normalized <= 1) niceNormalized = 1;
      else if (normalized <= 2) niceNormalized = 2;
      else if (normalized <= 5) niceNormalized = 5;
      else niceNormalized = 10;

      const step = niceNormalized * magnitude;

      const niceMin = Math.floor(minVal / step) * step;
      const niceMax = Math.ceil(maxVal / step) * step;

      const ticks = [];
      for (let i = niceMin; i <= niceMax + step / 2; i += step) {
        ticks.push(Number(i.toFixed(2)));
      }

      return ticks;
    }

    const yTicks = generateNiceTicks(minVal, maxVal, 5);

    return {
      title: "Revenue to EBIT",
      raw,
      yTicks
    };
  }, [financialsData]);

  console.log(convertRevenueToEBITChart, "*****")


  const [hover, setHover] = React.useState(false);

  return (


    <Box sx={{
      ml: 0,
      transition: "margin-left 0.25s ease",
      "& .MuiBox-root": { backgroundImage: "none" }
    }} style={{

      padding: isMobile ? '8px' : '0px 16px 0px 16px',
      fontFamily: 'inter !important'
    }}>
      {/* Loading Backdrop */}
      <Backdrop
        open={loading}
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <Box
        sx={{
          height: {
            xs: "calc(100dvh - 140px)", // mobile header
            md: "calc(100vh - 64px)",  // desktop header
          },
          overflowY: "auto",
          // px: isMobile ? 1 : 2,
          pb: "100px",
          /* smooth scrolling */
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch", // iOS momentum

          /* hide scrollbar */
          scrollbarWidth: "none",          // Firefox
          msOverflowStyle: "none",         // IE / Edge
          "&::-webkit-scrollbar": {
            display: "none",               // Chrome / Safari
          },
        }}
      >
        {/* Section Title: KPI's */}
        <Typography sx={{ mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px' }}>Financials / </span> <span style={{ fontWeight: 200, fontSize: '16px' }}>KPI's</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <KpiMetricscards cardsData={Kpiscardsdata} />

        {/* Section Title: Time Series */}
        <Typography sx={{ mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px' }}>Financials / </span> <span style={{ fontWeight: 200, fontSize: '16px' }}>Time Series</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

        {/* Section Title: Highlights with YTD/QTD/MTD buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 3 }}>
          <Typography>
            <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px' }}>Financials / </span> <span style={{ fontWeight: 200, fontSize: '16px' }}>Highlights</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['YTD', 'QTD', 'MTD'].map((label, idx) => (
              <FilterChip
                key={label}
                label={label}
                idx={idx}
                activeIdx={highlightsActiveIdx}
                onClick={handleHighlightsChipClick}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <HighlightsCards data={highlightCards} />
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography >
              <span style={{ color: "#1672B980", fontWeight: 200, fontSize: '16px' }}>Financials / </span> <span style={{ fontWeight: 200, fontSize: '16px' }}>Visualizations</span>
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {["YTD", "QTD", "MTD"].map((label, idx) => (
                <FilterChip
                  key={label}
                  label={label}
                  idx={idx}
                  activeIdx={visualizationActiveIdx}
                  onClick={handleVisualizationChipClick}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        </Box>
        {/* Section Title: Visualizations */}
        <Grid
          container
          rowSpacing={2}
          columnSpacing={2}
          sx={{
            mt: "26px !important",
            alignItems: "stretch"   // keeps all cards same height
          }}
        >
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
            pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
          }}>
            <PieLegendCard title="Gross Profit by Category" data={pieCategory} />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }}>
            <PieLegendCard title="Gross Profit by Sub Brand" data={pieSubBrands} />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }}>
            <PieLegendCard title="Top 5 Distributors by Profitability" data={pieDistributorsData} />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }}>
            <PieLegendCard title="Top 5 Retailers by Profitability" data={pieRetailersData} />
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "30px !important" }}>
          <Grid item xs={12}>
            {convertSubBrandDataDynamic?.xLabels?.length > 0 &&
              convertSubBrandDataDynamic?.series?.length > 0 &&
              convertSubBrandDataDynamic?.dataMatrix?.length > 0 && (

                <Barcharts
                  title={convertSubBrandDataDynamic.title}
                  xLabels={convertSubBrandDataDynamic.xLabels}
                  series={convertSubBrandDataDynamic.series}
                  dataMatrix={convertSubBrandDataDynamic.dataMatrix}
                  yTicks={convertSubBrandDataDynamic.yTicks}
                  yAxisLabel="Net Sales ($M)"
                  rounded={true}
                />

              )}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "30px !important" }}>
          <Grid item xs={12}>
            {convertNetGrossCashChart?.xLabels?.length > 0 &&
              convertNetGrossCashChart?.series?.length > 0 &&
              convertNetGrossCashChart?.dataMatrix?.length > 0 && (
                <LineChartsale
                  title={convertNetGrossCashChart.title}
                  xLabels={convertNetGrossCashChart.xLabels}
                  series={convertNetGrossCashChart.series}
                  dataMatrix={convertNetGrossCashChart.dataMatrix}
                  yTicks={convertNetGrossCashChart?.yTicks}
                  fillColor="#62BBFF"
                  yAxisLabel="Dollars ($M)"
                />
              )}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "30px !important" }}>
          <Grid item xs={12}>
            {convertInventoryChartData?.title &&
              convertInventoryChartData?.xLabels?.length > 0 &&
              convertInventoryChartData?.series?.length > 0 &&
              convertInventoryChartData?.dataMatrix?.length > 0 && (
                <InventoryCharts
                  title={convertInventoryChartData.title}
                  xLabels={convertInventoryChartData.xLabels}
                  series={convertInventoryChartData.series}
                  dataMatrix={convertInventoryChartData.dataMatrix}
                  yTicks={convertInventoryChartData?.yTicks}
                />
              )}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "30px !important" }}>
          <Grid item xs={12}>
            {convertRevenueToEBITChart &&
              convertRevenueToEBITChart.raw &&
              convertRevenueToEBITChart.raw.length > 0 && (
                <WaterfallChart
                  title={convertRevenueToEBITChart.title}
                  rawData={convertRevenueToEBITChart.raw}
                  yTicks={convertRevenueToEBITChart.yTicks}
                />
              )}

          </Grid>
        </Grid>

      </Box>


    </Box>



  );
};

export default FinancialDashboard;